import parse from "html-react-parser"
import PropTypes from "prop-types"
import React from "react"

import CoreHeading from "@knowit-alma/common/src/components/atoms/CoreHeading/CoreHeading"
import CoreList from "@knowit-alma/common/src/components/atoms/CoreList/CoreList"
import CoreParagraph from "@knowit-alma/common/src/components/atoms/CoreParagraph/CoreParagraph"
import CoreQuote from "@knowit-alma/common/src/components/atoms/CoreQuote/CoreQuote"

const Classic = ({ content = "" }) => {
  /**
   * Handle inline elements, like <a>, <em> and <strong>
   */
  const inlineElementHandler = ({ node, nested = false }) => {
    if (!node) {
      return
    }
    const { href, target, rel, children } = node?.props || {}
    const childrenArray = Array.isArray(children) ? children : [children]

    // const space = nested ? "" : " "
    const space = ""

    if (typeof node === "string") {
      return node
    }
    if (node && typeof node === "object") {
      switch (node.type) {
        case "br":
          return "<br />"
        case "a":
          return `${space}<a href="${href}" target="${target}" rel="${rel}">${childrenArray
            .map((item, i) => {
              return inlineElementHandler({
                node: item,
                nested: true,
              })?.replace(/<[^>]+>/g, "")
            })
            .join("")}</a>${space}`
        case "em":
        case "i":
          return `${space}<em>${childrenArray
            .map((item, i) => {
              return inlineElementHandler({
                node: item,
                nested: true,
              })?.replace(/<(?!br\s*\/?)[^>]+>/g, "")
            })
            .join("")}</em>${space}`
        case "strong":
        case "b":
          return `${space}<strong>${childrenArray
            .map((item, i) => {
              return inlineElementHandler({
                node: item,
                nested: true,
              })?.replace(/<(?!br\s*\/?)[^>]+>/g, "")
            })
            .join("")}</strong>${space}`
        case "u":
          return childrenArray
            .map((item, i) => {
              return inlineElementHandler({
                node: item,
                nested: true,
              })?.replace(/<[^>]+>/g, "")
            })
            .join("") // We'll ignore u
        default:
          return null
      }
    }
    return null
  }

  /**
   * Handle lists
   */
  const listHandler = ({ childrenArray, type = "ul" }) => {
    if (!Array.isArray(childrenArray)) {
      return
    }

    const items = childrenArray
      .map((item, i) => {
        const { children } = item?.props || {}
        const childrenArray = Array.isArray(children) ? children : [children]
        if (typeof item === "object") {
          let subElements = ""
          if (children?.type === "ul" || children?.type === "ol") {
            const listChildren = children?.props?.children
            const listChildrenArray = Array.isArray(listChildren)
              ? listChildren
              : [listChildren]

            subElements = listHandler({
              childrenArray: listChildrenArray,
              type: children.type,
            })
            //console.log(childrenArray)
            //console.log(subElements)
          } else {
            subElements = childrenArray
              .map((node, index) => {
                if (node?.type === "ul" || node?.type === "ol") {
                  const { children } = node?.props || {}
                  const childrenArray = Array.isArray(children)
                    ? children
                    : [children]
                  return listHandler({ childrenArray, type: node.type })
                } else {
                  return inlineElementHandler({ node })
                }
              })
              .join("")
          }

          return `<li>${subElements}</li>`
        }
        return null
      })
      .join("")
    return `<${type}>${items}</${type}>`
  }

  /**
   * Handle paragraphs
   */
  const paragraphHandler = ({ childrenArray }) => {
    if (!Array.isArray(childrenArray)) {
      return
    }
    const paragraph = childrenArray
      .map((node, index) => {
        return inlineElementHandler({ node })
      })
      .join("")
    return `<p>${paragraph}</p>`
  }

  /**
   * Handle block level elements
   */
  const blockHandler = ({ block, i }) => {
    if (!block) {
      return
    }

    let key = i.toString()

    const { children, className } = block?.props || {}
    const childrenArray = Array.isArray(children) ? children : [children]

    if (block) {
      switch (block.type) {
        case "ul":
        case "ol":
          return (
            <CoreList
              key={key}
              html={listHandler({ childrenArray, type: block.type })}
            />
          )
        case "blockquote":
          const quote = childrenArray
            .map((item, i) => {
              const { href, target, rel, children } = item.props || {}
              const childrenArray = Array.isArray(children)
                ? children
                : [children]
              if (typeof item === "string") {
                return item
              }
              if (typeof item === "object") {
                switch (item.type) {
                  case "a":
                    return `<a href="${href}" target="${target}" rel="${rel}">${children}</a>`
                  case "p":
                    return paragraphHandler({ childrenArray })
                  default:
                    return null
                }
              }
              return null
            })
            .filter(Boolean)
            .join("")
          return <CoreQuote key={key} quote={quote} />
        case "p":
          // Special case:  <p><img /></p>

          if (typeof children === "object" && children?.type === "img") {
            //const { alt, src, width, height } = children.props || {}
            // TODO: most likely does not work in Alma Insights without tweaks
            /*
            return (
              <CoreImage alt={alt} src={src} width={width} height={height} />
            )
            */
            //console.log("Classic: image placeholder")
            return <div></div>
          }
          return (
            <CoreParagraph
              key={key}
              html={paragraphHandler({ childrenArray })}
            />
          )
        case "h1":
        case "h2":
        case "h3":
        case "h4":
        case "h5":
        case "h6":
          const headingText = childrenArray
            .map((item, i) => {
              return inlineElementHandler({ node: item })?.replace(
                /<[^>]+>/g,
                ""
              )
            })
            .join("")

          if (!headingText) {
            // console.log(children)
          }
          return <CoreHeading key={key} text={headingText} level={block.type} />
        case "figure":
          if (className?.includes("wp-caption")) {
            return blockHandler({ block: children[0], i: i + 10000 })
          }
          return null
        case "div":
          // TODO?

          return null
        default:
          return null
      }
    }
  }

  /**
   * Parse the HTML to React elements
   *
   * TODO: sanitize the input in the frontend?
   * Tried isomorphic-dompurify, ran into an issue:
   * https://github.com/kkomelin/isomorphic-dompurify/issues/54
   * Maybe we don't need frontend sanitization here at all
   */
  const rawBlocks = parse(content, { trim: true })
  const rawBlocksArray = Array.isArray(rawBlocks) ? rawBlocks : [rawBlocks]
  // console.log(rawBlocks)
  /**
   * Convert the React elements to components
   */
  const blocks = rawBlocksArray
    .map((block, i) => {
      return blockHandler({ block, i })
    })
    .filter(Boolean)

  return blocks
}
export default Classic

Classic.propTypes = {
  content: PropTypes.string,
}
